import { useMemo, useState } from 'react';

import type { ModalChildrenProps } from '@bitstopco/bitstop-theme';
import { formatDate } from '@bitstopco/bitstop-theme/helpers';

import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  Divider,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextareaAutosize,
  Typography,
} from '@mui/material';
import toast from 'react-hot-toast';

import { DATE_FORMATS } from '@/constants/app/date';

import {
  money,
  originalTxCount,
  originalWorth,
  simulateItems,
  simulatedTxCount,
  simulatedWorth,
  txCount,
} from '@/helpers/cashLogs';

import {
  useGetCashLogQuery,
  useInvalidateCashLogMutation,
  useSimulateInvalidateClearQuery,
} from '@/store/api/services/machines/cashlogs';

import type { BoxClearSimulatedItemView } from '@/types';

const fmtUtc = (iso?: string) =>
  iso ? `${formatDate(iso, DATE_FORMATS.fullLocal, { timeZone: 'UTC' })} UTC` : '';

const DENOMS = [
  { key: 'one', value: 1 },
  { key: 'two', value: 2 },
  { key: 'five', value: 5 },
  { key: 'ten', value: 10 },
  { key: 'twenty', value: 20 },
  { key: 'fifty', value: 50 },
  { key: 'hundred', value: 100 },
] as const;

type DenomKey = (typeof DENOMS)[number]['key'];
type DenomCounts = Partial<Record<DenomKey, number>>;

/** Cell text for a denomination count: '-' for zero, '+N' for a signed gain. */
const fmtCount = (n: number, signed: boolean) =>
  n === 0 ? '-' : signed && n > 0 ? `+${n}` : `${n}`;

/** Per-denomination delta (simulated − original) for a simulate item. */
const denomDeltas = (item: BoxClearSimulatedItemView): DenomCounts => {
  const out: DenomCounts = {};
  for (const d of DENOMS) {
    out[d.key] = (item.simulated_values?.[d.key] ?? 0) - (item.original_values?.[d.key] ?? 0);
  }
  return out;
};

/**
 * A clear is affected when any denomination or its transaction count differs
 * between original/simulated (bill-less windows still move transactions).
 */
const hasChange = (item: BoxClearSimulatedItemView) =>
  DENOMS.some(
    (d) => (item.simulated_values?.[d.key] ?? 0) !== (item.original_values?.[d.key] ?? 0),
  ) || txAbsorbed(item) > 0;

/** Transactions a target gains from the invalidation; 0 when counts aren't reported. */
const txAbsorbed = (item: BoxClearSimulatedItemView) => {
  const before = originalTxCount(item);
  const after = simulatedTxCount(item);
  return before != null && after != null ? Math.max(after - before, 0) : 0;
};

/** Whether the backend reported both sides of a target's transaction count. */
const hasTxCounts = (item: BoxClearSimulatedItemView) =>
  originalTxCount(item) != null && simulatedTxCount(item) != null;

/** Projected delta for a target, DEV-1611 style: "+$70 / +2 tx" (worth only without counts). */
const deltaText = (item: BoxClearSimulatedItemView) => {
  const worth = `+${money(simulatedWorth(item) - originalWorth(item))}`;
  return hasTxCounts(item) ? `${worth} / +${txCount(txAbsorbed(item))}` : worth;
};

/**
 * Invalidate Cash Log modal.
 *
 * Renders a rebalance preview computed by the athena simulate endpoint
 * (`DELETE /v2/boxClears/{id}/simulate`) never client-side then commits via
 * boro (`PATCH /v2/CashBox/{id}/invalidate`). Reason is required and written to
 * the audit log.
 *
 * Transaction counts (DEV-1862) follow the DEV-1611 copy: the source row shows
 * its current tx count and each target its "+$X / +N tx" delta. They are hidden
 * when the simulate payload doesn't carry counts.
 */
const InvalidateCashLog = ({ hide, props }: ModalChildrenProps) => {
  const clearId = (props as { id?: number })?.id ?? 0;

  const [reason, setReason] = useState('');

  // The clear being invalidated is NOT part of its own simulate response: the
  // response is the *resulting* neighbourhood with that clear already removed.
  // So read its current totals from the detail endpoint to render the source row.
  const { data: clearResp, isFetching: loadingClear } = useGetCashLogQuery(String(clearId), {
    skip: !clearId,
  });
  const clear = clearResp?.data;

  const {
    data: simulation,
    isFetching: loadingPreview,
    isError: previewError,
  } = useSimulateInvalidateClearQuery(clearId, { skip: !clearId });

  const [invalidateCashLog, { isLoading: submitting }] = useInvalidateCashLogMutation();

  // Use the whole payload: every clear whose denominations change is an absorbing
  // target. Each carries the per-denomination delta (the bills that move into it)
  // and the side it sits on relative to the invalidated clear — the impact a
  // finance reviewer needs to sign off on the invalidation.
  const targets = useMemo(() => {
    const sourceTime = new Date(clear?.created_at ?? 0).getTime();
    return simulateItems(simulation)
      .filter(hasChange)
      .map((item) => ({
        item,
        deltas: denomDeltas(item),
        // The open period (no id / timestamp) is always after the invalidated clear.
        dir: (item.box_clear_id != null && new Date(item.created_at ?? 0).getTime() < sourceTime
          ? 'previous'
          : 'next') as 'previous' | 'next',
      }));
  }, [simulation, clear]);

  const sourceWorth = clear?.total_worth_of_bills ?? 0;

  // The invalidated clear's transactions all move into its neighbours, so its
  // count is what the targets absorb. Undefined when the backend omits counts.
  const totalTxAbsorbed = useMemo(() => {
    const reported = targets.filter((t) => hasTxCounts(t.item));
    if (reported.length === 0) return undefined;
    return reported.reduce((sum, t) => sum + txAbsorbed(t.item), 0);
  }, [targets]);
  const sourceCounts: DenomCounts = useMemo(() => {
    const out: DenomCounts = {};
    if (clear) for (const d of DENOMS) out[d.key] = clear[d.key] ?? 0;
    return out;
  }, [clear]);

  // One matrix row for the invalidated clear (bills leaving) plus one per
  // absorbing neighbour (bills arriving, as +deltas).
  const rows: RebalanceRow[] = useMemo(() => {
    if (!clear) return [];
    const list: RebalanceRow[] = [
      {
        key: 'source',
        title: `Clear #${clearId}`,
        createdAt: clear.created_at,
        role: 'Invalidated',
        accent: 'error.main',
        counts: sourceCounts,
        signed: false,
        worthText: `${money(sourceWorth)} → ${money(0)}`,
        detailText: totalTxAbsorbed != null ? txCount(totalTxAbsorbed) : undefined,
      },
    ];
    for (const t of targets) {
      // The absorbing target may be the open/running period — the simulate
      // endpoint returns it with no `box_clear_id`. Label it like the placement
      // preview ("Open period") instead of a blank "Clear #", and drop the
      // (mis-derived) direction since the open period is always the latest.
      const isOpenPeriod = t.item.box_clear_id == null;
      list.push({
        key: String(t.item.box_clear_id ?? t.item.cash_clear_id),
        title: isOpenPeriod ? 'Open period' : `Clear #${t.item.box_clear_id}`,
        createdAt: t.item.created_at,
        role: isOpenPeriod ? 'Absorbs' : `Absorbs · ${t.dir}`,
        accent: 'success.main',
        counts: t.deltas,
        signed: true,
        worthText: `${money(originalWorth(t.item))} → ${money(simulatedWorth(t.item))}`,
        detailText: deltaText(t.item),
      });
    }
    return list;
  }, [clear, clearId, sourceCounts, sourceWorth, targets, totalTxAbsorbed]);

  // One plain sentence: what moves, and into which clear (next / previous / open period).
  const summary = useMemo(() => {
    if (!clear || targets.length === 0) return '';
    const t = targets[0];
    const into =
      t.item.box_clear_id == null
        ? 'the open period'
        : `the ${t.dir} clear (Clear #${t.item.box_clear_id})`;
    const what =
      totalTxAbsorbed != null
        ? `${money(sourceWorth)} and ${totalTxAbsorbed === 1 ? '1 transaction' : `${totalTxAbsorbed.toLocaleString()} transactions`}`
        : money(sourceWorth);
    return `Clear #${clearId}'s ${what} will move into ${into}.`;
  }, [clear, targets, clearId, sourceWorth, totalTxAbsorbed]);

  const reasonValid = reason.trim().length > 0;

  const handleConfirm = async () => {
    try {
      await invalidateCashLog({ id: clearId, reason: reason.trim() }).unwrap();
      toast.success(`Clear #${clearId} invalidated`);
      hide();
    } catch {
      toast.error('Could not invalidate this cash log. Please try again.');
    }
  };

  return (
    <>
      <DialogContent sx={{ my: 2 }}>
        <Typography variant="body2" color="text.secondary" mb={2}>
          This marks the clear as invalid; its transactions are rebalanced into the closest valid
          adjacent clear.
        </Typography>

        <Alert
          severity="warning"
          icon={<WarningAmberIcon />}
          sx={{ mb: 2, alignItems: 'flex-start' }}
        >
          <Typography variant="body2">
            <strong>This action cannot be undone from the UI.</strong> The backend rebalances all
            assigned transactions into the closest valid adjacent clear.
          </Typography>
        </Alert>

        <Box
          sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden', mb: 2 }}
          data-test="cash-log-invalidate-preview"
        >
          <Box
            sx={{
              px: 1.75,
              py: 1.25,
              bgcolor: 'grey.50',
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Typography
              variant="caption"
              fontWeight={600}
              color="text.secondary"
              sx={{ letterSpacing: '0.06em', textTransform: 'uppercase' }}
            >
              Rebalance preview
            </Typography>
          </Box>

          {loadingPreview || loadingClear ? (
            <Stack p={1.5} gap={1.5}>
              <Skeleton variant="rounded" height={56} />
              <Skeleton variant="rounded" height={56} />
            </Stack>
          ) : previewError ? (
            <Box sx={{ px: 1.75, py: 1.5 }}>
              <Typography variant="caption" color="error.main">
                Could not load the rebalance preview. Review the adjacent clears before
                invalidating.
              </Typography>
            </Box>
          ) : !clear ? (
            <Box sx={{ px: 1.75, py: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                No preview available for this clear.
              </Typography>
            </Box>
          ) : targets.length > 0 || sourceWorth > 0 ? (
            <>
              <RebalanceTable rows={rows} />
              <Box sx={{ px: 1.75, py: 1.25, borderTop: 1, borderColor: 'divider' }}>
                {targets.length === 0 ? (
                  <Typography variant="caption" color="warning.main">
                    No adjacent valid clear found. These bills will be unassigned.
                  </Typography>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    {summary}
                  </Typography>
                )}
              </Box>
            </>
          ) : (
            <Box sx={{ px: 1.75, py: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                This clear is empty. There's no cash to rebalance.
              </Typography>
            </Box>
          )}
        </Box>

        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Reason
          </Typography>
          <TextareaAutosize
            minRows={4}
            maxRows={8}
            autoFocus
            placeholder="Provide a reason for invalidating this clear..."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-test="cash-log-invalidate-reason"
            style={{
              width: '100%',
              resize: 'vertical',
              padding: '12px 14px',
              border: '1px solid rgba(0, 0, 0, 0.23)',
              borderRadius: '4px',
              fontSize: '0.875rem',
              fontFamily: 'inherit',
              lineHeight: '1.5',
              backgroundColor: 'transparent',
              outline: 'none',
              cursor: 'text',
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            Written to the audit log. Required for any invalidation.
          </Typography>
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Button onClick={hide}>Cancel</Button>
        <LoadingButton
          variant="contained"
          color="error"
          loading={submitting}
          disabled={!reasonValid || loadingPreview}
          onClick={handleConfirm}
          data-test="cash-log-invalidate-submit"
        >
          Invalidate clear
        </LoadingButton>
      </DialogActions>
    </>
  );
};

interface RebalanceRow {
  key: string;
  /** Row heading — "Clear #N" for a real clear, "Open period" for the running bucket. */
  title: string;
  createdAt?: string;
  role: string;
  accent: 'error.main' | 'success.main';
  /** Per-denomination value to show: bills leaving (source) or +deltas (target). */
  counts: DenomCounts;
  signed: boolean;
  worthText: string;
  /** Source: current tx count. Target: "+$X / +N tx" delta (story copy). Omitted when unknown. */
  detailText?: string;
}

const headSx = {
  py: 0.75,
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: 'text.secondary',
} as const;
const numSx = { textAlign: 'right', fontVariantNumeric: 'tabular-nums' } as const;

/**
 * Nested denomination table for a single clear: $1…$100 columns with the bill
 * count under each. Source rows show bills leaving; target rows show +deltas.
 */
const DenomBreakdownTable = ({ counts, signed }: { counts: DenomCounts; signed: boolean }) => (
  <Table size="small" sx={{ width: 'auto', '& td, & th': { border: 0, px: 1, py: 0.25 } }}>
    <TableHead>
      <TableRow>
        {DENOMS.map((d) => (
          <TableCell
            key={d.key}
            sx={{ ...numSx, fontSize: 10, fontWeight: 700, color: 'text.secondary' }}
          >
            ${d.value}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
    <TableBody>
      <TableRow>
        {DENOMS.map((d) => {
          const n = counts[d.key] ?? 0;
          return (
            <TableCell
              key={d.key}
              sx={{
                ...numSx,
                fontFamily: 'monospace',
                color:
                  n === 0 ? 'text.disabled' : signed && n > 0 ? 'success.dark' : 'text.primary',
              }}
            >
              {fmtCount(n, signed)}
            </TableCell>
          );
        })}
      </TableRow>
    </TableBody>
  </Table>
);

/**
 * Per-clear impact view for the invalidation: one outer row per affected clear
 * (the invalidated one, then each absorbing neighbour), each holding a nested
 * denomination breakdown and the worth before → after. This is the audit view a
 * finance reviewer signs off on.
 */
const RebalanceTable = ({ rows }: { rows: RebalanceRow[] }) => (
  <Table size="small" sx={{ '& > tbody > tr:last-of-type > td': { borderBottom: 0 } }}>
    <TableHead>
      <TableRow>
        <TableCell sx={headSx}>Clear</TableCell>
        <TableCell sx={headSx}>Bill denomination breakdown</TableCell>
        <TableCell sx={{ ...headSx, ...numSx }}>Worth</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {rows.map((r) => (
        <TableRow key={r.key}>
          <TableCell
            sx={{ borderLeft: '3px solid', borderLeftColor: r.accent, verticalAlign: 'top' }}
          >
            <Typography variant="body2" fontWeight={600}>
              {r.title}
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', color: r.accent }}>
              {r.role}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {fmtUtc(r.createdAt)}
            </Typography>
          </TableCell>
          <TableCell sx={{ verticalAlign: 'top' }}>
            <DenomBreakdownTable counts={r.counts} signed={r.signed} />
          </TableCell>
          <TableCell
            sx={{
              ...numSx,
              fontFamily: 'monospace',
              fontWeight: 600,
              color: r.accent,
              verticalAlign: 'top',
            }}
          >
            {r.worthText}
            {r.detailText && (
              <Typography
                variant="caption"
                color={r.accent}
                sx={{ display: 'block', fontFamily: 'monospace' }}
              >
                {r.detailText}
              </Typography>
            )}
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

export default InvalidateCashLog;
